import { useEffect, useState } from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { emptyJobForm, serviceError, contactError, toPayload } from "@/lib/jobForm";
import { ServiceStep } from "@/components/booking/ServiceStep";
import { ContactStep } from "@/components/booking/ContactStep";
import { ClientLookup } from "@/components/booking/ClientLookup";
import { QuoteCard } from "@/components/booking/QuoteCard";
import { PageHeader } from "@/pages/admin/AdminLayout";
import { ConfirmationActions } from "@/pages/admin/AssignDialog";
import { HelpersPicker } from "@/components/HelpersPicker";

const Block = ({ n, title, children }) => (
  <section className="rounded-2xl border bg-white p-5 sm:p-7">
    <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#61a229]">{n}</p>
    <h2 className="mb-5 mt-1 font-display text-xl font-semibold">{title}</h2>
    {children}
  </section>
);

export default function ManualJob() {
  const [config, setConfig] = useState(null);
  const [techs, setTechs] = useState([]);
  const [form, setForm] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    const [first = "", ...rest] = (q.get("name") || "").trim().split(" ");
    return { ...emptyJobForm(), kind: "intervento", phone: q.get("phone") || "", first_name: first, last_name: rest.join(" ") };
  });
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(null);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    api.get("/public/config", { params: { all: true } }).then((r) => setConfig(r.data));
    api.get("/admin/technicians").then((r) => setTechs(r.data.filter((t) => t.active)));
  }, []);

  const upd = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const multiDay = form.kind === "intervento" && !!config?.equipment.find((e) => e.key === form.equipment_type)?.multi_day;

  const submit = async (force = false) => {
    const err = serviceError(form) || contactError(form, false) || (!form.date ? "Inserisci la data" : null) || (!form.start || !form.end ? "Inserisci gli orari" : null)
      || (multiDay && (!form.end_date || form.end_date < form.date) ? "Inserisci una data di fine valida" : null);
    if (err) return toast.error(err);
    setBusy(true);
    setBlocked(null);
    try {
      const { data } = await api.post("/admin/jobs", { ...toPayload({ ...form, dates: [] }), end_date: multiDay ? form.end_date : null, force });
      setCreated(data);
      const what = "Appuntamento";
      toast.success(data.status === "confermato" ? `${what} creato e assegnato` : `${what} creato (in attesa di assegnazione)`);
      setForm({ ...emptyJobForm(), kind: form.kind });
    } catch (e) {
      if (e?.response?.status === 409) setBlocked(e.response.data.detail);
      else toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Inserimento manuale" title="Nuovo appuntamento" />
      <p className="-mt-4 mb-5 text-sm text-slate-500">Dall'ufficio puoi scegliere qualsiasi data e orario, anche fuori dalle fasce e dagli orari dei tecnici.</p>
      <div className="grid gap-6 xl:grid-cols-12">
        <div className="space-y-6 xl:col-span-8">
          <Block n="01" title="Servizio"><ServiceStep form={form} set={setForm} config={config} />
            <label className="mt-4 flex w-fit items-center gap-2 rounded-xl border bg-white px-4 py-2.5 text-sm font-medium" data-testid="manual-warranty">
              <input type="checkbox" checked={!!form.warranty} onChange={(e) => setForm((p) => ({ ...p, warranty: e.target.checked }))} className="h-4 w-4 accent-[#61a229]" data-testid="manual-warranty-checkbox" />
              Intervento in garanzia
            </label></Block>
          <Block n="02" title="Cliente e posizione"><ClientLookup form={form} setForm={setForm} /><ContactStep form={form} set={setForm} /></Block>
          <Block n="03" title="Pianificazione">
            <div className="grid gap-4 sm:grid-cols-4">
              <div className="space-y-2 sm:col-span-2"><Label>{multiDay ? "Data inizio *" : "Data *"}</Label><Input type="date" value={form.date} onChange={upd("date")} data-testid="manual-date-input" /></div>
              {multiDay && <div className="space-y-2 sm:col-span-2"><Label>Data fine * (prorogabile)</Label><Input type="date" min={form.date || undefined} value={form.end_date || ""} onChange={upd("end_date")} data-testid="manual-end-date-input" /></div>}
              <div className="space-y-2"><Label>Dalle *</Label><Input type="time" value={form.start} onChange={upd("start")} data-testid="manual-start-input" /></div>
              <div className="space-y-2"><Label>Alle *</Label><Input type="time" value={form.end} onChange={upd("end")} data-testid="manual-end-input" /></div>
              <div className="space-y-2 sm:col-span-4"><Label>Tecnico (opzionale, conferma subito)</Label>
                <Select value={form.technician_id || "none"} onValueChange={(v) => setForm((p) => ({ ...p, technician_id: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="manual-tech-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuno — lascia in attesa</SelectItem>
                    {techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.technician_id && (
                <div className="sm:col-span-4">
                  <HelpersPicker techs={techs} leadId={form.technician_id} value={form.helper_ids || []} onChange={(v) => setForm((p) => ({ ...p, helper_ids: v }))} />
                </div>
              )}
            </div>
            {blocked && (
              <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="manual-conflict-alert">
                <p className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" /> {blocked.message}</p>
                <ul className="mt-1 list-disc pl-6">{blocked.conflicts?.map((c, i) => <li key={i}>{c.message}</li>)}</ul>
                <Button size="sm" className="mt-3 rounded-full bg-red-600 hover:bg-red-700" onClick={() => submit(true)} disabled={busy} data-testid="manual-force-button">Crea comunque, ignora disponibilità</Button>
              </div>
            )}
          </Block>
        </div>
        <aside className="space-y-4 xl:col-span-4 xl:sticky xl:top-8 self-start">
          <QuoteCard form={form} config={config} />
          <Button onClick={() => submit(false)} disabled={busy} className="w-full rounded-full" data-testid="manual-submit-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Salva appuntamento
          </Button>
          {created && <div className="rounded-2xl border bg-white p-4 space-y-3" data-testid="manual-created">
            <p className="text-sm">Creato per <b>{created.client.first_name} {created.client.last_name}</b></p>
            <ConfirmationActions job={created} /></div>}
        </aside>
      </div>
    </div>
  );
}
